import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { provinceStateService } from "../services/provinceStateService";
import { provinceStateDisplayId } from "./ProvinceStateCommonStore";
import { ProvinceState } from "../../../api/graphql";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { countryService } from "../../country/services/countryService";
import { InitializeParams } from "../../../store/types";

export class ProvinceStateViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = provinceStateService;
  provinceStateInstance: ProvinceState | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  name_esValue: string = "";
  name_enValue: string = "";
  name_ptValue: string = "";
  countryService = countryService;
  countrySearchValue: string = "";
  countryIdValue: string = "";
  countrySelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadProvinceStateById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.provinceStateOne({ id });
    this.provinceStateInstance = result.provinceStateOne;
    if (this.provinceStateInstance) {
      this.loadValues(this.provinceStateInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.provinceStateInstance) {
      return provinceStateDisplayId(this.provinceStateInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.ProvinceStateSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "country") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.countrySelectedValue = country;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get countryValue() {
    if (this.countrySelectedValue) {
      return {
        id: this.countrySelectedValue.id,
        displayId: countryDisplayId(this.countrySelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.provinceStateInstance = null;
    this.name_esValue = "";
    this.name_enValue = "";
    this.name_ptValue = "";
    this.countrySearchValue = "";
    this.countryIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelProvinceState() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("provinceState_update");
  }

  editProvinceState() {
    const id = this.provinceStateInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/provinceState/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printProvinceState(handlePrint: () => void): void {
    handlePrint();
  }

  *closeProvinceState(): any {
    yield this.close();
  }

  loadValues(instance: ProvinceState) {
    this.idValue = instance.id;
    this.name_esValue = instance.name_es;
    this.name_enValue = instance.name_en;
    this.name_ptValue = instance.name_pt;
    this.countrySelectedValue = instance.country;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadProvinceStateById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
